import { FC, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { closeCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { RedesignButton } from '@components/RedesignButton';
import { Text } from '@components/Text';
import { useHistory } from 'react-router-dom';
import { updateSiteData } from 'src/redesign/features/SelectedSite/store/actions';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';
import { showNotification } from 'src/redesign/features/Toast/store/actions';
import S from './TrialOrderLimitModal.module.scss';

type TrialOrderLimitModalProps = {
  orderCount?: number;
  limit?: number;
  title?: string;
};

export const TrialOrderLimitModal: FC<TrialOrderLimitModalProps> = ({
  orderCount = 10,
  limit = 10,
  title,
}) => {
  const dispatch = useAppDispatch();
  const selectedSite = useAppSelector(getSelectedSiteDataSelector);
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const handleMoveToFreePlan = async () => {
    if (!selectedSite?.id) return;

    try {
      setLoading(true);
      await dispatch(
        updateSiteData({
          siteId: selectedSite.id,
          siteData: {
            trial_ended_free_plan_selected: true,
          },
        }),
      ).unwrap();

      dispatch(
        showNotification({
          detail:
            'Site moved to Free plan. You can now create free events with up to 20 attendees per month.',
          severity: 'success',
          life: 5000,
        }),
      );

      dispatch(closeCurrentModalAction());
    } catch (error) {
      dispatch(
        showNotification({
          detail: 'Failed to update site. Please try again.',
          severity: 'error',
          life: 5000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    dispatch(closeCurrentModalAction());
    history.push('/events/billing');
  };

  // Determine if this is a trial ended scenario vs order limit reached
  const isTrialEnded = title?.includes('Trial Ended');
  const displayTitle = isTrialEnded
    ? 'Your trial ended — upgrade to keep selling tickets'
    : 'Trial limit reached — upgrade to keep selling tickets';

  return (
    <div className={S.trialOrderLimitModal}>
      <div className={S.content}>
        <Text bold size="lg" className={S.title}>
          {displayTitle}
        </Text>

        <div className={S.urgencyBox}>
          <Text bold className={S.urgencyText}>
            ⚠️ New orders are paused — upgrade to resume sales
          </Text>
          <Text className={S.urgencySubtext}>Don't miss out on ticket sales.</Text>
        </div>

        <div className={S.descriptionSection}>
          <Text className={S.description}>
            {orderCount > 0 && (
              <>
                You've already processed <strong>{orderCount} orders</strong> during your
                trial.{' '}
              </>
            )}
            Your event is ready to sell — <strong>upgrade now</strong> to continue accepting orders.
          </Text>
        </div>

        <div className={S.options}>
          {/* PRIMARY OPTION - Paid Plan */}
          <div className={`${S.option} ${S.primaryOption}`}>
            <div className={S.optionHeader}>
              <Text bold size="md">
                Keep selling tickets (Upgrade)
              </Text>
              <span className={S.recommendedBadge}>RECOMMENDED</span>
            </div>
            <ul className={S.featureList}>
              <li>Accept unlimited paid orders</li>
              <li>Keep 100% of your ticket revenue (no platform fees)</li>
              <li>Unlock seating, waitlists, and analytics</li>
              <li>Customize your event pages and checkout</li>
            </ul>
          </div>

          {/* SECONDARY OPTION - Free Plan */}
          <div className={`${S.option} ${S.secondaryOption}`}>
            <Text bold size="md" className={S.secondaryTitle}>
              Switch to Free (No paid tickets)
            </Text>
            <ul className={S.featureList}>
              <li>Free events only</li>
              <li>Limited to 20 attendees/month</li>
              <li>No advanced features</li>
            </ul>
          </div>
        </div>
      </div>

      <div className={S.controlsContainer}>
        <button onClick={handleMoveToFreePlan} disabled={loading} className={S.freeLinkButton}>
          Continue with Free
        </button>
        <RedesignButton
          onClick={handleUpgrade}
          variation="primary"
          disabled={loading}
          height={52}
          className={S.primaryButton}
        >
          <Text bold color="white-100" size="lg">
            Upgrade & Keep Selling
          </Text>
        </RedesignButton>
      </div>
    </div>
  );
};
